import { format, startOfMonth } from 'date-fns';
import { useState } from 'react';

import { useRfidCards } from '@/api/cards';
import { useClusteredChargingSummary } from '@/api/chargingActivity';
import { TimePicker } from '@/components/TimePicker';
import { DateFrame, type TimeFrame, TimePickerType } from '@/components/TimePicker/types';
import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import { ChargingCardsStatusPanel } from '@/features/dashboard/components/ChargingCardStatusPanel';
import { EnergyChartPanel } from '@/features/dashboard/components/EnergyChartPanel';
import { FeedbackBanner } from '@/features/dashboard/components/FeedbackBanner';
import { MapViewPanel } from '@/features/dashboard/components/MapViewPanel';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { t } from '@/lib/utils';

import SupportModal from '../chargingActivity/components/SupportModal';

export const Dashboard = () => {
    const currentDate = new Date();

    const [timeFrame, setTimeFrame] = useState<TimeFrame>({
        startDate: startOfMonth(currentDate),
        endDate: currentDate,
        frame: {
            timeEnum: DateFrame.MONTH,
            timeLabel: t('general.filter.thisMonth'),
        },
    });

    const { data: sessions, isLoading: isLoadingSessions } = useClusteredChargingSummary({
        localStartDate: format(timeFrame.startDate ?? new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx"),
        localEndDate: format(timeFrame.endDate ?? new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx"),
    });

    const { data, isLoading: isLoadingCards } = useRfidCards();
    const cards = data?.cards || [];

    const { value: isFeedbackBannerEnabled } = useFeatureToggle(FeatureToggles.ENABLE_FEEDBACK_BANNER);

    return (
        <div>
            <ChargingCardsStatusPanel cards={cards} isLoading={isLoadingCards} />
            <div className='display-flex flex-column margin-top-25'>
                <div className='display-flex align-items-end justify-content-between'>
                    <TimePicker
                        disabled={isLoadingSessions}
                        timePickerType={TimePickerType.SHORT}
                        timeFrame={timeFrame}
                        setTimeFrame={setTimeFrame}
                        callback={(startDate, endDate) => {
                            if (startDate && endDate) {
                                setTimeFrame(prev => ({ ...prev, startDate, endDate }));
                            }
                        }}
                    />
                    <SupportModal />
                </div>
                <div className='margin-bottom-25 margin-top-10 display-flex width-100pct flex-column'>
                    <div className='display-flex width-100pct gap-20'>
                        <div className='panel display-flex width-66pct flex-column justify-content-center'>
                            <EnergyChartPanel sessions={sessions} isLoading={isLoadingSessions} timeFrame={timeFrame} />
                        </div>
                        <MapViewPanel />
                    </div>
                </div>
            </div>
            {isFeedbackBannerEnabled && <FeedbackBanner />}
        </div>
    );
};
